import { IVzdEntry } from '@alberta/konexi-shared';
import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { IKimRecipient, IKimRecipientFilter, IKimRecipientPrefill } from 'src/app/shared/models/kim/kim.model';
import { KimApiService, VzdUnavailableError } from 'src/app/shared/services/kim/kim-api.service';

import { KimRecipientFilterComponent } from './kim-recipient-filter/kim-recipient-filter.component';

export interface IKimRecipientSearchResult {
  recipient?: IKimRecipient;
  /**
   * Outcome of the last search attempt: `true` when the directory could not be queried,
   * `false` when it answered. Left out when no search ran, so the host keeps its current state.
   */
  vzdUnavailable?: boolean;
}

@Component({
  selector: 'itl-kim-recipient-search',
  templateUrl: './kim-recipient-search.modal.html',
  styleUrls: ['./kim-recipient-search.modal.scss'],
})
export class KimRecipientSearchModal implements OnInit {
  @Input() prefill?: IKimRecipientPrefill;

  public searchTerm = '';
  public filter: IKimRecipientFilter = {};
  public recipients: IKimRecipient[] = [];
  public searching = false;
  public hasSearched = false;

  private _dismissed = false;
  private _pendingSearch = false;
  private _searchSucceeded = false;

  public get filterActive(): boolean {
    return Boolean(this.filter.street || this.filter.postalCode || this.filter.locality);
  }

  constructor(
    private _modalController: ModalController,
    private _kimApiService: KimApiService
  ) {}

  public ngOnInit(): void {
    if (this.prefill?.q) {
      this.searchTerm = this.prefill.q;
    }
    if (this.prefill?.filter) {
      this.filter = { ...this.prefill.filter };
    }
  }

  public async ionViewDidEnter(): Promise<void> {
    if (this.searchTerm || this.filterActive) {
      await this.search();
    }
  }

  public async search(): Promise<void> {
    if (this.searching) {
      // A search is already running (e.g. the auto-search from
      // ionViewDidEnter). Queue this request instead of dropping it silently,
      // so a filter change or reset applied in the meantime is not lost.
      this._pendingSearch = true;
      return;
    }

    const term = this.searchTerm?.trim();
    if (!term && !this.filterActive) {
      return;
    }

    this.searching = true;
    try {
      const result = await this._kimApiService.searchVzd({
        q: term || undefined,
        street: this.filter.street,
        postalCode: this.filter.postalCode,
        locality: this.filter.locality,
      });
      this.recipients = this.toRecipients(result.entries);
      this.hasSearched = true;
      this._searchSucceeded = true;
    } catch (error) {
      // The empty state is reserved for a directory that answered with no hits. Anything else
      // hands the failure back to the host, which shows the retryable info box.
      if (!(error instanceof VzdUnavailableError)) {
        window.logger.error('KIM VZD search failed', error);
      }
      await this.dismissOnce({ vzdUnavailable: true });
      return;
    } finally {
      this.searching = false;
      if (this._pendingSearch && !this._dismissed) {
        this._pendingSearch = false;
        await this.search();
      }
    }
  }

  public async select(recipient: IKimRecipient): Promise<void> {
    await this.dismissOnce({ recipient });
  }

  public async cancel(): Promise<void> {
    // Report a reachable directory so the host can drop an info box it showed earlier.
    await this.dismissOnce(this._searchSucceeded ? { vzdUnavailable: false } : undefined);
  }

  public async openFilter(): Promise<void> {
    const modal = await this._modalController.create({
      component: KimRecipientFilterComponent,
      componentProps: { filter: this.filter },
      backdropDismiss: false,
      cssClass: 'fullPageModal',
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    if (result.data) {
      this.filter = result.data as IKimRecipientFilter;
      await this.search();
    }
  }

  public async resetFilter(): Promise<void> {
    this.filter = {};
    await this.search();
  }

  public formatAddress(recipient: IKimRecipient): string {
    const location = [recipient.postalCode, recipient.locality].filter(Boolean).join(' ');
    return [recipient.street, location].filter(Boolean).join(', ');
  }

  /**
   * Einträge ohne KIM-Adresse werden nicht angezeigt. Einträge mit
   * mehreren KIM-Adressen werden auf eine Zeile pro Adresse aufgefächert.
   */
  private toRecipients(entries: IVzdEntry[]): IKimRecipient[] {
    return (entries ?? [])
      .filter(entry => entry.kimAddresses?.length)
      .flatMap(entry =>
        entry.kimAddresses.map(kimAddress => ({
          displayName: this.toDisplayName(entry),
          telematikId: entry.telematikId,
          kimAddress,
          street: entry.street,
          postalCode: entry.postalCode,
          locality: entry.locality,
        }))
      );
  }

  private toDisplayName(entry: IVzdEntry): string {
    if (entry.displayName) {
      return entry.displayName;
    }
    const personName = [entry.title, entry.givenName, entry.surname].filter(Boolean).join(' ');
    return personName || entry.organization || entry.telematikId || entry.uid;
  }

  /**
   * Ensures this modal instance dismisses at most once. Without this guard, a
   * second dismiss call (e.g. from a second failed search) could close
   * whatever modal happens to be on top by then, such as the parent dialog
   * that presented this search modal.
   */
  private async dismissOnce(data?: IKimRecipientSearchResult): Promise<void> {
    if (this._dismissed) {
      return;
    }
    this._dismissed = true;
    await this._modalController.dismiss(data);
  }
}
